import React from 'react';
import { Switch } from '@ark-ui/react/switch';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface SwitchInputProps extends BaseInputProps<boolean> {
  /** Volitelný popisek vedle přepínače. */
  switchLabel?: string;
  className?: string;
}

/**
 * Atomický přepínač (toggle) postavený na Ark UI `Switch`.
 *
 * Přístupné jméno je deterministické přes `aria-labelledby` (stejný vzor jako
 * {@link CheckboxInput} — FormField popisek + volitelný vnitřní popisek).
 */
export const SwitchInput: React.FC<SwitchInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  describedBy,
  labelId,
  testAttributes,
  switchLabel,
  className,
}) => {
  const checked = !!value;
  const innerLabelId = `${id}-inner-label`;

  return (
    <Switch.Root
      checked={checked}
      disabled={disabled}
      readOnly={readOnly}
      invalid={hasError}
      onCheckedChange={(details) => onChange(details.checked)}
      onBlur={onBlur}
      className={clsx(styles.switchRoot, className)}
      data-selected={checked ? 'true' : 'false'}
      ids={{ hiddenInput: id, label: innerLabelId }}
    >
      <Switch.Control className={styles.switchControl}>
        <Switch.Thumb className={styles.switchThumb} />
      </Switch.Control>
      {switchLabel && (
        <Switch.Label className={styles.switchLabel}>{switchLabel}</Switch.Label>
      )}
      <Switch.HiddenInput
        ref={ref}
        name={name}
        aria-labelledby={switchLabel ? `${labelId} ${innerLabelId}` : labelId}
        aria-describedby={describedBy}
        {...testAttributes}
      />
    </Switch.Root>
  );
};

SwitchInput.displayName = 'SwitchInput';
